/**
 * Auto Shortlist — "Top Picks for You".
 *
 * Why this exists:
 *   Users can flip Lens, Priority, and Budget — but the map's job isn't
 *   done until it actually *answers* "so where should I buy / rent / look
 *   first?". Without a shortlist the user has to mentally rank 60–115
 *   suburbs themselves. This module composes the three active signals into
 *   a ranked top 3–5 that reads as "these are my best options" rather than
 *   "everything has some signal".
 *
 * Composition:
 *   score =  lensScore    (0..10)   × 1.0   — persona fit from tags
 *          + priorityScore (0..10)  × 1.0   — already-computed priority-fit
 *          + budgetScore   (0..10)  × 0.8   — Good/Stretch/Weak → 10/5/0
 *          + verdictBonus (-8..+3)          — safety/quality truth nudge
 *
 * Rules:
 *   - Pure function. No DOM, no storage reads, no side effects.
 *   - Copy is delegated — we call the existing `interpretForMode` and
 *     `constrainBestFor` / `constrainTradeoff` pipeline so the shortlist
 *     never invents its own copy. That keeps every fact/priority/budget
 *     guard we already wrote honest.
 *   - Verdict is NOT a hard gate. A Red suburb with genuinely top-tier
 *     lens+priority+budget fit can surface; its `verdictBonus` of -8 will
 *     usually push it out, but a clearly-best Red suburb earning its seat
 *     is honest — the card shows its red pin and tradeoff, so the negative
 *     signal is never hidden.
 *   - Minimum-signal gate: if Priority === "none" AND band === "none", we
 *     return an empty shortlist. Lens alone is not enough commitment for
 *     a confident "top picks" claim.
 */

import type { Verdict } from "../data/verdicts";
import type { LensMode } from "./decision-lens";
import type { Priority } from "./constraint-priority";
import type { BudgetBand, UserBudgetRange } from "./budget-band";
import { priorityFit } from "./priority-fit";
import { classifyFit } from "./budget-school-fit";
import { interpretForMode } from "./single-area-lens";
import { constrainBestFor, constrainTradeoff } from "./constraint-layer";
import { decidePrimaryCards } from "./why-this-beats-that";
import { extractFacts } from "./suburb-facts";
import type { PriceBand } from "./suburb-facts";
import type { PurchaseMode } from "./purchase-mode";
import type { PropertyType } from "./property-type";
import {
  budgetBucketDef,
  pickPriceMedian,
  type BudgetBucket,
  type CityCode,
} from "./budget-format";

export interface ShortlistItem {
  id: string;
  name: string;
  verdict: Verdict["verdict"];
  score: number;
  reason: string;
  tradeoff: string;
}

export interface ShortlistContext {
  lens: LensMode;
  priority: Priority;
  band: BudgetBand;
  /**
   * Fine-grained budget bucket. The band is the coarse 3-tier mapping that
   * scoring and the classifier read; the bucket is the exact numeric
   * window the user picked (e.g. "b0 = Up to A$600k"). Shortlist eligibility
   * is gated on the bucket, not the band — that's the contract users expect
   * when they select "Up to A$600k" and then see only ≤A$600k suburbs.
   */
  bucket: BudgetBucket;
  /**
   * The user's EXACT numeric [lo,hi] window from the range-bar. When set,
   * scoring + gating use these numbers directly instead of collapsing to
   * the nearest bucket — see `budgetScore` / `bucketFit` for the
   * realestate.com.au-style gradient ("closer to hi scores higher").
   * `null` means the user hasn't engaged the slider (or selected "Any");
   * callers fall back to the legacy bucket-centric path.
   */
  userRange?: UserBudgetRange | null;
  /** Active city — needed to pick the right bucket bounds and median unit. */
  city: CityCode;
  /** Buy vs Rent — picks which median field to read. */
  purchaseMode: PurchaseMode;
  /** House / Apartment / Any — picks which median field to read. */
  propertyType: PropertyType;
}

/** Max items in a shortlist — deliberately small so the map stays legible. */
export const SHORTLIST_MAX = 5;

/** Minimum items required for the panel to render as a list (vs empty-state). */
export const SHORTLIST_MIN = 3;

/** Score floor: even with 5 slots, we refuse to pad with sub-floor suburbs. */
export const SHORTLIST_SCORE_FLOOR = 9;

/* -------------------------------------------------------------------------- */
/*  Lens scoring — tag-driven, mirrors the single-area-lens tag cascades.     */
/* -------------------------------------------------------------------------- */

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

function lensScore(v: Verdict, lens: LensMode): number {
  const t = tagSet(v);

  // Exploration — no persona, so no lens-specific boost. buildShortlist's
  // caller (`hasEnoughSignal`) already refuses to run in this mode, so
  // this branch is defensive and shouldn't surface to the user.
  if (lens === "none") return 0;

  if (lens === "family") {
    // Positive: catchment, family, leafy, village, school-catchment.
    // Negative: nightlife, cbd, bars, industrial-adjacent.
    let s = 0;
    if (t.has("school-catchment")) s += 6;
    if (t.has("family")) s += 4;
    if (t.has("leafy")) s += 3;
    if (t.has("village")) s += 2;
    if (t.has("mature-infra") || t.has("heritage")) s += 1;
    if (t.has("beach") || t.has("waterfront")) s += 1;
    if (t.has("nightlife") || t.has("bars")) s -= 4;
    if (t.has("cbd")) s -= 3;
    if (t.has("industrial-adjacent") || t.has("arterial-noise")) s -= 2;
    if (t.has("flood-risk") || t.has("bushfire-risk")) s -= 2;
    return clampScore(s);
  }

  if (lens === "bachelor") {
    // Positive: nightlife, cafe-culture, food-scene, bars, cbd, tram-corridor,
    // transit, beach/waterfront. Negative: school-catchment, leafy, estate.
    let s = 0;
    if (t.has("nightlife") || t.has("bars")) s += 5;
    if (t.has("cafe-culture") || t.has("food-scene")) s += 4;
    if (t.has("cbd") || t.has("inner-city")) s += 4;
    if (t.has("it-corridor")) s += 3;
    if (t.has("tram-corridor") || t.has("transit") || t.has("metro-tunnel")) s += 3;
    if (t.has("beach") || t.has("waterfront")) s += 2;
    if (t.has("activity-centre") || t.has("retail-hub")) s += 2;
    if (t.has("school-catchment") || t.has("family")) s -= 2;
    if (t.has("estate") || t.has("new-build") || t.has("growth-corridor")) s -= 2;
    return clampScore(s);
  }

  // investor
  // Positive: growth-corridor, it-corridor, transit, activity-centre,
  // nightlife (rental demand), beach/waterfront. Negative: flood/bushfire,
  // premium with oversupply.
  let s = 0;
  if (t.has("growth-corridor")) s += 5;
  if (t.has("new-build") || t.has("estate")) s += 3;
  if (t.has("it-corridor")) s += 4;
  if (t.has("transit") || t.has("activity-centre") || t.has("tram-corridor")) s += 3;
  if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene")) s += 2;
  if (t.has("beach") || t.has("waterfront")) s += 2;
  if (t.has("school-catchment") || t.has("family")) s += 1; // low-churn tenants
  if (t.has("infra-lag")) s -= 2;
  if (t.has("flood-risk") || t.has("bushfire-risk")) s -= 3;

  // Researched-evidence tier — overrides tag inference when present.
  // The tier is set in `verdicts.ts` / `verdicts-mel.ts` based on hotspot
  // reports, infrastructure-pipeline tenders, school-catchment strength,
  // employment-corridor growth, supply constraints, and migration data.
  // We apply it as a bonus (not a hard override) so tag signals still
  // contribute — the tier is the *direction*, not the whole answer.
  //
  //   stronger → +6   (well-evidenced long-term upside)
  //   moderate → +3   (plausible upside, mixed signals)
  //   weaker   → -2   (evidence points to flat/soft or mature plateau)
  //
  // This is explicitly NOT a price forecast. The field name — "tier" —
  // ranks *relative* upside, and the copy layer (single-area-lens)
  // surfaces the drivers/risks so the user sees *why*.
  const ev = v.investorEvidence;
  if (ev) {
    if (ev.tier === "stronger") s += 6;
    else if (ev.tier === "moderate") s += 3;
    else if (ev.tier === "weaker") s -= 2;
  }
  return clampScore(s);
}

function clampScore(n: number): number {
  if (n < 0) return 0;
  if (n > 10) return 10;
  return n;
}

/* -------------------------------------------------------------------------- */
/*  Budget scoring — realestate.com.au semantics                              */
/*                                                                            */
/*  When the user has dragged the range-bar (ctx.userRange set), score is a   */
/*  continuous gradient within [lo,hi] that rewards proximity to `hi` — a     */
/*  suburb sitting at the ceiling of the user's budget scores higher than a   */
/*  suburb well below it. This mirrors how every real-estate portal with a    */
/*  price-range filter treats the upper bound as the user's aspiration.       */
/*                                                                            */
/*  Without userRange we fall back to the coarse band-level classifier so     */
/*  legacy callers (dropdown-only pickers, older bookmarks) keep working.     */
/* -------------------------------------------------------------------------- */

function budgetScore(v: Verdict, ctx: ShortlistContext): number {
  if (ctx.userRange) {
    return budgetScoreWithinRange(v, ctx);
  }
  if (ctx.band === "none") return 0;
  const fit = classifyFit(v, ctx.band);
  if (fit === "good") return 10;
  if (fit === "stretch") return 5;
  return 0; // weak or null
}

/**
 * Gradient scorer. Reads the researched median for the (city, mode, type)
 * combo and maps it onto a 0..10 scale, peaking at `userRange.hi`.
 *
 *   median ≤ lo       → 2      (below floor — user said min X for a reason)
 *   median ∈ (lo,hi)  → 4..10  (linear rise, aspiration-weighted)
 *   median = hi       → 10
 *   median ∈ (hi,hi+halo) → 6  (stretch — above budget but within halo)
 *   median > hi+halo  → 0      (out of reach)
 *
 * When the suburb has no researched median for this (mode, type), fall back
 * to the coarse band classifier — we'd rather apply the old logic than
 * silently give every unpriced suburb a zero.
 */
function budgetScoreWithinRange(v: Verdict, ctx: ShortlistContext): number {
  const fmtCtx = {
    city: ctx.city,
    mode: ctx.purchaseMode,
    type: ctx.propertyType,
  };
  const median = v.priceData ? pickPriceMedian(fmtCtx, v.priceData) : null;
  if (median === null) {
    // Unknown price — keep legacy behaviour so unpriced suburbs aren't
    // automatically excluded by a missing data field.
    if (ctx.band === "none") return 0;
    const fit = classifyFit(v, ctx.band);
    if (fit === "good") return 10;
    if (fit === "stretch") return 5;
    return 0;
  }
  const { lo, hi } = ctx.userRange!;
  if (hi <= lo) return 0; // defensive — range-bar never commits this

  if (median >= lo && median <= hi) {
    // Linear rise from 4 at lo to 10 at hi. Floor of 4 (not 0) means a
    // suburb barely inside the window still outranks most out-of-range
    // options, while the ceiling-favouring slope does the real work.
    const t = (median - lo) / (hi - lo);
    return 4 + t * 6;
  }
  if (median < lo) {
    // Below the user's floor — they said "spend AT LEAST X" for a reason.
    // We don't exclude outright (the bucket gate does that), just score
    // low so below-floor suburbs never anchor the list.
    return 2;
  }
  // Above hi — eligible only if within the stretch halo.
  const halo = stretchHalo(ctx.city, ctx.purchaseMode);
  if (median <= hi + halo) return 6;
  return 0;
}

/* -------------------------------------------------------------------------- */
/*  Price-band gate — hard eligibility for the main "Top Picks" list          */
/* -------------------------------------------------------------------------- */

export type PriceFit = "good" | "stretch" | "weak";

/**
 * Classify a suburb's fit against the user's budget band using the SAME
 * `PriceBand` the practical-price badge reads from (`extractFacts(v).price.
 * band`). This guarantees consistency between what the badge says and
 * whether the suburb qualifies for Top Picks:
 *
 *   - Suburb band ≤ user band      → "good"    (included in Top Picks)
 *   - Suburb band exactly 1 above  → "stretch" (shown under "Closest
 *                                                stretch options")
 *   - Suburb band 2+ above         → "weak"    (excluded from both lists)
 *
 * Returns null when band === "none" — in that case there is no budget
 * constraint and the caller should not gate.
 */
const PRICE_TIER: Record<Exclude<PriceBand, "unknown">, 0 | 1 | 2> = {
  entry: 0,
  mid: 1,
  premium: 2,
};

export function priceBandFit(v: Verdict, band: BudgetBand): PriceFit | null {
  if (band === "none") return null;
  const pb = extractFacts(v).price.band;
  const suburbTier = pb === "unknown" ? 1 : PRICE_TIER[pb];
  const userTier: 0 | 1 | 2 =
    band === "tight" ? 0 : band === "moderate" ? 1 : 2;
  const gap = suburbTier - userTier;
  if (gap <= 0) return "good";
  if (gap === 1) return "stretch";
  return "weak";
}

/* -------------------------------------------------------------------------- */
/*  Bucket-level price gate — the SOURCE OF TRUTH for shortlist eligibility.  */
/*                                                                            */
/*  Why this replaces `priceBandFit` for Top-Picks eligibility                */
/*  ──────────────────────────────────────────────────────────                */
/*  The 3-tier band (`tight` / `moderate` / `stretch`) was deliberately       */
/*  coarse — it carries the scoring weight and the classifier vocabulary,     */
/*  and that stays unchanged. But the USER'S budget is the bucket they just   */
/*  tapped ("Up to A$600k") — a 600-wide window, not a 900-wide band. A       */
/*  suburb whose researched median is A$820k must NEVER appear in the         */
/*  "Up to A$600k" Top Picks list just because it scored well and the band    */
/*  mapping said "tight". This function closes that gap.                      */
/*                                                                            */
/*  The check reads the researched median from `v.priceData` — the central    */
/*  pricing bank guarantees every visible suburb has one. It compares that    */
/*  median to the exact `[lo, hi]` of the bucket the user selected. Bucket    */
/*  "any" skips the gate entirely (no budget commitment).                     */
/* -------------------------------------------------------------------------- */

/**
 * The width of the "stretch" halo above the user's bucket — a suburb whose
 * median exceeds the bucket's hi by less than this much is surfaced under
 * "Just above your budget", not in Top Picks. Defined per-city/mode so the
 * unit ("A$k" vs "₹Lakh" vs "$/wk") lands on a meaningful figure.
 */
function stretchHalo(city: CityCode, mode: PurchaseMode): number {
  if (city === "mel") return mode === "buy" ? 200 : 100; // A$200k  |  $100/wk
  if (city === "syd") return mode === "buy" ? 300 : 150; // A$300k  |  $150/wk (Sydney premium tail)
  // hyd & blr — Indian-rupee units.
  return mode === "buy" ? 25 : 10;                        // ₹25L    |  ₹10k/mo
}

/**
 * Strict bucket-level fit.
 *
 * Returns:
 *   - "good"    — median ∈ [bucket.lo, bucket.hi]        → eligible for Top Picks
 *   - "stretch" — median ∈ (bucket.hi, bucket.hi+halo]   → eligible ONLY for stretch section
 *   - "weak"    — median > bucket.hi + halo  OR  median < bucket.lo
 *   - null      — no eligibility constraint (bucket === "any")
 *
 * When the suburb has no researched median for the current (mode, type)
 * combo, we fall back to the legacy `priceBandFit` so eligibility is never
 * silently blocked by missing data. In practice every visible suburb now
 * carries pricing data via the central bank, so this fallback is defensive.
 */
export function bucketFit(v: Verdict, ctx: ShortlistContext): PriceFit | null {
  // Prefer the lossless userRange when the slider has been engaged — this is
  // the fix for "user picks 500K-1M, system collapses to bucket b1 (600-700k)
  // and shortlists cheap suburbs". When userRange is set, we gate against
  // the EXACT numbers the user dragged.
  const fmtCtx = {
    city: ctx.city,
    mode: ctx.purchaseMode,
    type: ctx.propertyType,
  };

  if (ctx.userRange) {
    const { lo, hi } = ctx.userRange;
    const median = v.priceData ? pickPriceMedian(fmtCtx, v.priceData) : null;
    if (median === null) {
      // Unknown median — fall through to the coarse band fit so we still
      // honour the user's budget intent without dropping unpriced suburbs.
      return priceBandFit(v, ctx.band);
    }
    if (median > hi) {
      const halo = stretchHalo(ctx.city, ctx.purchaseMode);
      if (median <= hi + halo) return "stretch";
      return "weak";
    }
    if (median < lo) return "weak";
    return "good";
  }

  // Legacy path — bucket-centric gating for the dropdown and for callers
  // that haven't wired userRange yet.
  if (ctx.bucket === "any") return null;
  const def = budgetBucketDef(fmtCtx, ctx.bucket);
  const median = v.priceData ? pickPriceMedian(fmtCtx, v.priceData) : null;

  if (median === null) {
    // No researched median for this (mode, type) combo — fall through to
    // the coarse band fit so we still honour the user's budget intent.
    return priceBandFit(v, ctx.band);
  }

  // Strict upper gate — suburbs above the bucket's top are NEVER "good".
  if (median > def.hi) {
    const halo = stretchHalo(ctx.city, ctx.purchaseMode);
    if (median <= def.hi + halo) return "stretch";
    return "weak";
  }
  // Below the bucket's lower edge — the user said "I can spend AT LEAST X";
  // we treat well-below-floor as weak-fit to keep the list focused.
  if (median < def.lo) {
    // "Up to X" buckets have lo = 0 so this branch only fires for middle/high
    // buckets where the user has picked a specific window.
    return "weak";
  }
  return "good";
}

/* -------------------------------------------------------------------------- */
/*  Verdict nudge — safety/quality truth pulls toward green, away from red.   */
/* -------------------------------------------------------------------------- */

function verdictBonus(v: Verdict): number {
  if (v.verdict === "green") return 3;
  if (v.verdict === "amber") return 0;
  return -8; // red
}

/* -------------------------------------------------------------------------- */
/*  Composite scoring                                                         */
/* -------------------------------------------------------------------------- */

export interface ShortlistRankInput {
  v: Verdict;
  ctx: ShortlistContext;
}

export interface ShortlistRank {
  v: Verdict;
  score: number;
  parts: {
    lens: number;
    priority: number;
    budget: number;
    verdict: number;
  };
}

export function rankSuburb(
  v: Verdict,
  ctx: ShortlistContext
): ShortlistRank {
  const lens = lensScore(v, ctx.lens);
  const priority =
    ctx.priority === "none" ? 5 : priorityFit(v, ctx.priority).score;
  const budget = budgetScore(v, ctx);
  const verdict = verdictBonus(v);
  // When the user has dragged the slider to a specific numeric range, we
  // raise the budget weight from 0.8 → 1.2. This matches realestate.com.au
  // behaviour: a user who committed to "up to X" expects the suburbs
  // *closest to X* to win, not a slightly-cheaper one that happens to have
  // a marginally better lens score. Without the uplift, a 4-point lens
  // edge can overwhelm a 3-point ceiling-proximity advantage and the list
  // anchors in the middle of the range instead of the top.
  // Legacy callers without userRange keep the original 0.8 weight, so
  // the dropdown-only path scores exactly as before.
  const budgetWeight = ctx.userRange ? 1.2 : 0.8;
  const score = lens * 1.0 + priority * 1.0 + budget * budgetWeight + verdict;
  return { v, score, parts: { lens, priority, budget, verdict } };
}

/* -------------------------------------------------------------------------- */
/*  Shortlist builder                                                         */
/* -------------------------------------------------------------------------- */

/**
 * True when the user has committed enough preference for a shortlist to be
 * meaningful. Any non-default signal qualifies — Lens, Priority, OR Budget.
 *
 * Pre-2026-05 this was lens-only ("Decision mode requires a persona"). That
 * matched the original product framing but quietly broke a common flow: the
 * user opens the filter, drags the BUDGET slider (a very direct "show me
 * what fits"), closes the page, and sees… nothing. The map pins don't fade,
 * the picks panel stays hidden, the slider's commit reaches storage but
 * nothing surfaces. Users read this as "the price filter is broken."
 *
 * Now any single committed signal flips the shortlist on. Picks rank with
 * `lensScore = 0` when lens is "none" (see `lensScore`) — the ranking still
 * works, it just doesn't tilt toward a persona.
 */
export function hasEnoughSignal(ctx: ShortlistContext): boolean {
  return (
    ctx.lens !== "none" ||
    ctx.priority !== "none" ||
    ctx.bucket !== "any" ||
    ctx.userRange !== null
  );
}

/**
 * Build the user-facing shortlist.
 *
 * Returns an empty array when signal is thin (see `hasEnoughSignal`). The
 * caller (shortlist-panel) shows an empty-state message in that case.
 */
export function buildShortlist(
  dataset: readonly Verdict[],
  ctx: ShortlistContext
): ShortlistItem[] {
  if (!hasEnoughSignal(ctx)) return [];

  let ranked: ShortlistRank[] = dataset
    .map((v) => rankSuburb(v, ctx))
    .filter((r) => r.score >= SHORTLIST_SCORE_FLOOR)
    .sort((a, b) => b.score - a.score);

  // HARD budget gate — Top Picks must COMPLY with the exact selected
  // bucket, not just the mapped 3-tier band. Selecting "Up to A$600k" must
  // NEVER surface a ~A$820k suburb in Top Picks. We compare the researched
  // median (`priceData`) to the bucket's numeric bounds. Above-budget
  // suburbs drop to `buildStretchPicks` (rendered in a separate section).
  // "any" bucket returns null → no gating (user hasn't committed a budget).
  if (ctx.bucket !== "any") {
    ranked = ranked.filter((r) => bucketFit(r.v, ctx) === "good");
  }

  const top = ranked.slice(0, SHORTLIST_MAX);
  return applyCopy(top, ctx);
}

/**
 * "Closest stretch options" — suburbs that rank well on lens + priority
 * but sit exactly one budget band above the user. Rendered as a separate
 * section in the panel; NEVER mixed into the main Top Picks. Returns
 * empty when band === "none" or nothing qualifies as stretch.
 */
export function buildStretchPicks(
  dataset: readonly Verdict[],
  ctx: ShortlistContext
): ShortlistItem[] {
  if (!hasEnoughSignal(ctx)) return [];
  if (ctx.bucket === "any") return [];

  const ranked: ShortlistRank[] = dataset
    .map((v) => rankSuburb(v, ctx))
    .filter((r) => r.score >= SHORTLIST_SCORE_FLOOR)
    .filter((r) => bucketFit(r.v, ctx) === "stretch")
    .sort((a, b) => b.score - a.score);

  const top = ranked.slice(0, SHORTLIST_MAX);
  return applyCopy(top, ctx);
}

/**
 * Shared copy pipeline — turns ranked entries into ShortlistItems, then
 * overrides reason + tradeoff with fact-first uniqueness-enforced copy.
 * Extracted so the Top-Picks and Stretch lists format identically.
 */
function applyCopy(
  top: readonly ShortlistRank[],
  ctx: ShortlistContext
): ShortlistItem[] {
  const baseItems = top.map((r) => toItem(r, ctx));
  const copy = decidePrimaryCards(
    top.map((r) => r.v),
    ctx.priority
  );
  const copyById = new Map(copy.map((c) => [c.id, c]));
  return baseItems.map((item) => {
    const c = copyById.get(item.id);
    return c ? { ...item, reason: c.reason, tradeoff: c.tradeoff } : item;
  });
}

function toItem(r: ShortlistRank, ctx: ShortlistContext): ShortlistItem {
  const v = r.v;
  // Generate copy through the existing pipelines so every contradiction
  // guard (fact-first copy, no-rail precedence, etc) applies.
  const base = interpretForMode(v, ctx.lens);
  const reason = constrainBestFor(
    v,
    ctx.lens,
    ctx.priority,
    base.bestFor,
    ctx.band
  );
  const tradeoff = constrainTradeoff(
    v,
    ctx.lens,
    ctx.priority,
    base.tradeoff,
    ctx.band
  );
  return {
    id: v.id,
    name: v.name,
    verdict: v.verdict,
    score: round1(r.score),
    reason,
    tradeoff,
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Return just the IDs of the shortlist for map-layer gating. Equivalent to
 * `buildShortlist(...).map((i) => i.id)` but returned as a Set for O(1)
 * lookup when tagging per-feature `isShortlisted`.
 */
export function shortlistIds(
  dataset: readonly Verdict[],
  ctx: ShortlistContext
): Set<string> {
  return new Set(buildShortlist(dataset, ctx).map((i) => i.id));
}
